/**
 * WorkforceForecast — demand across the week from the three sources (events /
 * appointments / walk-in queues): per-day totals, an hourly profile for the
 * selected day, and the drivers behind it. Data: getDemandForecast (declared).
 */
import { Fragment, useMemo, useState } from "react";
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, EmptyState } from "../../shell/composites";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";
import { useWfResource, useBusy } from "./useWorkforce";
import { getDemandForecast, recalcDemandForecast, type ForecastResponse, type DayDemand } from "./wf-api";
import {
  WeekNav, LocationFilter, SourceLegend, SourceDot, DemandStack, WfPending,
  SOURCE_META, mondayOf, isoToday, dayLabel, hmRangeLabel,
} from "./wf-shared";

/** Staff-hours required in a day, per source (each slice is 30 min). */
function dayHours(d: DayDemand) {
  return d.slices.reduce(
    (acc, s) => ({ events: acc.events + s.events / 2, appointments: acc.appointments + s.appointments / 2, queue: acc.queue + s.queue / 2 }),
    { events: 0, appointments: 0, queue: 0 },
  );
}
const totalOf = (h: { events: number; appointments: number; queue: number }) => h.events + h.appointments + h.queue;

/* Demand heatmap · hour × day. Aggregated from the declared per-day slices
 * (no new data): each cell = total demand in that hour of that day. */
function DemandHeatmap({ days }: { days: DayDemand[] }) {
  const t = useT();
  const hourSet = new Set<number>();
  days.forEach((d) => d.slices.forEach((s) => hourSet.add(parseInt(s.t, 10))));
  const hours = [...hourSet].sort((a, b) => a - b);
  if (days.length === 0 || hours.length === 0) return null;
  const cell = (d: DayDemand, hour: number) => d.slices.filter((s) => parseInt(s.t, 10) === hour).reduce((a, s) => a + s.events + s.appointments + s.queue, 0);
  const max = Math.max(1, ...days.flatMap((d) => hours.map((h) => cell(d, h))));
  const hourLabel = (h: number) => (h > 12 ? `${h - 12}p` : h === 12 ? "12p" : `${h}a`);
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: "18px 20px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("workforce.forecast.demandHeatmap", "Demand heatmap")}</span>
        <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.forecast.hourByDayTotalDemand", "Hour × day · total demand across sources")}</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `44px repeat(${days.length}, minmax(40px, 1fr))`, gap: 3, minWidth: 420 }}>
          <span />
          {days.map((d) => <span key={d.date} style={{ fontSize: 10.5, fontWeight: 700, color: T.n2, textAlign: "center", paddingBottom: 2 }}>{dayLabel(d.date)}</span>)}
          {hours.map((h) => (
            <Fragment key={h}>
              <span style={{ fontSize: 10, color: T.soft, fontFamily: T.fontMono, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 6 }}>{hourLabel(h)}</span>
              {days.map((d) => {
                const v = cell(d, h); const intensity = v / max;
                return (
                  <div key={d.date + h} title={`${dayLabel(d.date)} ${hourLabel(h)} · ${v}`} style={{ height: 26, borderRadius: 4, background: v > 0 ? alpha(T.p1, Math.round(8 + intensity * 62)) : T.n6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontFamily: T.fontMono, fontWeight: 600, color: intensity > 0.55 ? T.white : v > 0 ? T.n1 : T.soft }}>{v || "·"}</div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WorkforceForecast() {
  const nav = useNav();
  const t = useT(); // i18n pilot surface 1 (kit v4.2): workforce.forecast.* keys
  const [weekStart, setWeekStart] = useState(() => mondayOf());
  const [locationId, setLocationId] = useState<string | null>(null);
  const [day, setDay] = useState(() => isoToday());
  const key = `${weekStart}|${locationId ?? "all"}`;
  const { data, loading, error, reload } = useWfResource<ForecastResponse>(`forecast:${key}`, () => getDemandForecast(weekStart, locationId ?? undefined));
  const { busy, run } = useBusy();
  const recalc = () => void run("recalc", async () => {
    try { await recalcDemandForecast(weekStart, locationId ?? undefined); toast({ tone: "success", title: t("workforce.forecast.modelRecalculated", "Model recalculated"), subtitle: t("workforce.forecast.forecastRefreshed", "Forecast refreshed from the latest signals") }); reload(); }
    catch (e) { toast({ tone: "danger", title: t("workforce.forecast.couldnTRecalculate", "Couldn't recalculate the model"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });

  const days = data?.days ?? [];
  const perDay = useMemo(() => days.map((d) => ({ date: d.date, hours: dayHours(d) })), [days]);
  const maxDay = Math.max(1, ...perDay.map((p) => totalOf(p.hours)));
  const selected = days.find((d) => d.date === day) ?? days[0];
  const peak = useMemo(() => {
    let best = { heads: 0, t: "", date: "" };
    days.forEach((d) => d.slices.forEach((s) => { const n = s.events + s.appointments + s.queue; if (n > best.heads) best = { heads: n, t: s.t, date: d.date }; }));
    return best;
  }, [days]);
  const requiredWk = perDay.reduce((s, p) => s + totalOf(p.hours), 0);
  const scheduledWk = (data?.staffedHours ?? []).reduce((s, r) => s + r.scheduled, 0);
  const sliceMax = Math.max(1, ...(selected?.slices ?? []).map((s) => s.events + s.appointments + s.queue));
  const busiest = perDay.reduce<{ date: string; total: number } | null>((b, p) => (!b || totalOf(p.hours) > b.total ? { date: p.date, total: totalOf(p.hours) } : b), null);

  const body = data && (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Week columns */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: "18px 20px 14px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("workforce.forecast.text")}</span>
          <span style={{ fontSize: 12, color: T.soft }}>{data.locationName}{data.accuracy?.last4wMape != null ? ` · forecast error last 4 wks ${data.accuracy.last4wMape}% MAPE` : ""}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, alignItems: "end" }}>
          {perDay.map((p) => {
            const active = selected?.date === p.date;
            const sh = (data.staffedHours ?? []).find((r) => r.date === p.date);
            const short = sh ? sh.scheduled < sh.required : false;
            const h = (n: number) => Math.round((n / maxDay) * 130);
            return (
              <button key={p.date} data-testid="wfforecast-day-col" data-id={p.date} onClick={() => setDay(p.date)}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, background: active ? T.p4 : "transparent", border: `1px solid ${active ? T.p2 : "transparent"}`, borderRadius: 8, padding: "10px 6px 8px", cursor: "pointer", fontFamily: T.font }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.n1, fontFamily: T.fontMono }}>{Math.round(totalOf(p.hours))}{t("workforce.forecast.h", "h")}</span>
                <span style={{ width: 26, display: "flex", flexDirection: "column-reverse", borderRadius: 3, overflow: "hidden" }}>
                  <span style={{ height: h(p.hours.appointments), background: SOURCE_META.appointments.color }} />
                  <span style={{ height: h(p.hours.queue), background: SOURCE_META.queue.color }} />
                  <span style={{ height: h(p.hours.events), background: SOURCE_META.events.color }} />
                </span>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: active ? T.p1 : T.n2 }}>{dayLabel(p.date)}</span>
                {sh ? (
                  <span style={{ fontSize: 10.5, color: short ? T.red1 : T.soft, fontFamily: T.fontMono }}>{Math.round(sh.scheduled)}/{Math.round(sh.required)}{t("workforce.forecast.h", "h")}{" "}{short ? t("workforce.workforceForecast.short", "short") : "✓"}</span>
                ) : <span style={{ fontSize: 10.5, color: T.soft }}>—</span>}
              </button>
            );
          })}
        </div>
      </div>

      <DemandHeatmap days={days} />

      {/* Hourly profile for the selected day */}
      {selected ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: "18px 20px" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("workforce.forecast.text2")} {dayLabel(selected.date, "full")}</span>
            <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.forecast.text3")}</span>
          </div>
          <div style={{ display: "flex", overflowX: "auto", paddingBottom: 4 }}>
            {selected.slices.map((s) => (
              <div key={s.t} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <DemandStack slice={s} max={sliceMax} width={22} height={70} />
                {s.t.endsWith(":00") && parseInt(s.t, 10) % 2 === 0 ? <span style={{ fontSize: 9.5, color: T.soft, fontFamily: T.fontMono }}>{parseInt(s.t, 10)}</span> : <span style={{ height: 12 }} />}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Drivers */}
      {selected ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("workforce.forecast.text4")}{" "}{dayLabel(selected.date)} </span>
            <span style={{ fontSize: 12, color: T.soft }}>{selected.drivers.length} {t("workforce.forecast.drivers", "drivers")}</span>
          </div>
          {selected.drivers.length === 0 ? (
            <div style={{ padding: 28, fontSize: 13, color: T.soft, textAlign: "center" }}>{t("workforce.forecast.text5")}</div>
          ) : selected.drivers.map((dr, i) => (
            <div key={dr.id} data-testid="wfforecast-driver-row" data-id={dr.id}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 20px", borderBottom: i === selected.drivers.length - 1 ? "none" : `1px solid ${T.n6}` }}>
              <SourceDot kind={dr.kind} size={9} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{dr.label}</span>
                <span style={{ display: "block", fontSize: 12, color: T.soft }}>{SOURCE_META[dr.kind].label} · {hmRangeLabel(dr.start, dr.end)}</span>
              </span>
              <span style={{ fontSize: 12.5, color: T.n2, fontFamily: T.fontMono, background: alpha(SOURCE_META[dr.kind].color, 12), borderRadius: 999, padding: "3px 10px" }}>{dr.heads} {dr.heads === 1 ? "head" : "heads"}</span>
              {dr.kind === "events" && dr.refId ? (
                <button data-testid="wfforecast-driver-open" onClick={() => nav.go("workforce.staffing.event", { id: dr.refId })}
                  style={{ border: `1px solid ${T.border}`, background: T.white, borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, color: T.n1, cursor: "pointer", fontFamily: T.font, boxShadow: T.shadowChrome }}>{t("workforce.forecast.text6")}</button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={<Icon.chart size={20} />} title={t("workforce.forecast.title")} body={t("workforce.forecast.body")} />
      )}
    </div>
  );

  return (
    <Screen rail="wf-forecast">
      <PageHeader title={t("workforce.forecast.title2")} subtitle={t("workforce.forecast.subtitle")} breadcrumb={null}
        actions={<div style={{ display: "flex", gap: 10, alignItems: "center" }}><SourceLegend /><Btn variant="secondary" size="sm" disabled={busy !== null || loading} onClick={recalc}><Icon.refresh size={13} /> {busy === "recalc" ? t("workforce.forecast.recalculating", "Recalculating…") : t("workforce.forecast.recalculateModel", "Recalculate model")}</Btn></div>} />
      <DataSourceStrip screen="wfforecast" ops={["getDemandForecast", "recalcDemandForecast"]} reqId="REQ-054" />
      {/* Atlas C1 — label a horizon by what it is FOR, not by its length, and
          never let a filtered forecast look unfiltered: "a wrong number
          presented confidently" is the failure mode this line prevents. The
          quarterly capacity-and-hiring horizon, the channel model, the LOB lens
          and the scenario sliders are all held pending REQ-102. */}
      <div style={{ margin: "12px 24px 0", padding: "10px 14px", borderRadius: 9, background: T.p4, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <Icon.info size={14} style={{ color: T.p1, flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.5 }}>
          <b style={{ color: T.n1 }}>{t("workforce.forecast.shiftPlanningHorizon", "Shift-planning horizon")}</b> — {t("workforce.forecast.horizonSub", "demand by half-hour for the selected week, which is the window you staff against.")}{" "}
          <b style={{ color: T.n1 }}>{locationId ? t("workforce.forecast.oneBranchInView", "One branch in view") : t("workforce.forecast.allBranchesInView", "All branches in view")}</b>
          {locationId ? ` — ${t("workforce.forecast.everyNumberFiltered", "every number below is filtered to it.")}` : ""}
        </span>
      </div>
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("workforce.forecast.label")} value={Math.round(requiredWk)} icon={<Icon.chart size={14} />} accent={T.p1} />
        <StatTile label={t("workforce.forecast.label2")} value={Math.round(scheduledWk)} icon={<Icon.clock size={14} />} accent={scheduledWk >= requiredWk ? T.green1 : T.orange1} />
        <StatTile label={t("workforce.forecast.label3")} value={peak.heads > 0 ? `${peak.heads}` : "—"} icon={<Icon.users size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("workforce.forecast.label4")} value={busiest ? dayLabel(busiest.date) : "—"} icon={<Icon.calendar size={14} />} accent={T.tPurple} />
      </div>
      <FilterBar>
        <LocationFilter value={locationId} onChange={setLocationId} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <WeekNav weekStart={weekStart} onChange={(ws) => { setWeekStart(ws); setDay(ws); }} testid="wfforecast-weeknav" />
      </FilterBar>
      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {error ? <WfPending screen="wfforecast" opName="getDemandForecast" error={error} onRetry={reload} />
          : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.forecast.text7")}</div>
          : body}
      </div>
    </Screen>
  );
}
